/**
 * Journey Maturity Panel
 *
 * Integrates all 5 maturity dashboard components following the UX design:
 * 1. MaturityScoreHero - Primary score display with gauge
 * 2. StationBreakdownGrid - Rich station cards (not simple list)
 * 3. IndustryComparisonBar - Benchmarking against industry (optional)
 * 4. TargetProgressCard - Goal tracking with ROI (optional)
 * 5. HistoricalTrendsChart - Trends over time (optional)
 *
 * @see Documentation/Development/Design Docs/Maturity_Scoring_System_UNIFIED_Design.md
 * @see client/components/MaturityDashboard.example.tsx
 */

import React, { useState, useEffect } from 'react';
import { useApiClient } from '../hooks/useApiClient';
import { useJourneyId } from '../contexts/JourneyContext';
import { MaturityScoreHero } from './MaturityScoreHero';
import { StationBreakdownGrid } from './StationBreakdownGrid';
import { IndustryComparisonBar } from './IndustryComparisonBar';
import { TargetProgressCard } from './TargetProgressCard';
import { HistoricalTrendsChart } from './HistoricalTrendsChart';
import type { MaturityLevel } from '../../server/scoring/maturityNormalizer';

/** Status values for target progress tracking */
type TargetProgressStatus = 'on_track' | 'at_risk' | 'overdue' | 'achieved';

/** Extended status that includes abandoned (used in API responses) */
type TargetApiStatus = TargetProgressStatus | 'abandoned';

interface MaturityScoreData {
  overall_score?: number;
  score?: number;
  fair_equivalent?: number;
  fairEquivalent?: number;
  classification?: string;
  created_at?: string;
  timestamp?: string;
  stationsCompleted?: number;
  stationsTotal?: number;
  delta?: string | number;
  stationScores?: Record<string, unknown>;
}

/** Percentile rank categories for industry comparison */
type PercentileRank = 'bottom_25' | 'below_median' | 'above_median' | 'top_25' | 'top_10';

/** Industry baseline percentile data */
interface IndustryBaseline {
  median: number;
  p25: number;
  p75: number;
  p90: number;
}

interface IndustryComparisonData {
  yourScore: number;
  industry: string;
  segment: string;
  baseline: IndustryBaseline;
  percentileRank: PercentileRank;
  deltaFromBaseline: number;
}

interface TargetData {
  targetScore: number;
  currentScore: number;
  targetDate: string;
  daysRemaining: number;
  status: TargetProgressStatus;
  gap: number;
  progressPct: number;
  expectedROI?: number;
  rationale?: string;
  requiredMonthlyRate?: number;
}

interface TargetApiData {
  targetScore: number;
  currentScore: number;
  targetDate: string;
  daysRemaining: number;
  status: TargetApiStatus;
  gap: number;
  progressPct: number;
  expectedROI?: number;
  rationale?: string;
  requiredMonthlyRate?: number;
}

interface HistoryData {
  score: number;
  timestamp: string;
  [key: string]: unknown;
}

interface JourneyMaturityPanelProps {
  journeyId?: string;
  organizationId?: string;
}

export const JourneyMaturityPanel: React.FC<JourneyMaturityPanelProps> = ({
  journeyId: journeyIdProp,
  organizationId
}) => {
  const journeyId = useJourneyId(journeyIdProp);
  const { authFetch } = useApiClient();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [latestScore, setLatestScore] = useState<MaturityScoreData | null>(null);
  const [industryComparison, setIndustryComparison] = useState<IndustryComparisonData | null>(null);
  const [activeTarget, setActiveTarget] = useState<TargetData | null>(null);
  const [history, setHistory] = useState<HistoryData[]>([]);

  useEffect(() => {
    async function fetchMaturityData() {
      if (!journeyId) return;

      try {
        setLoading(true);
        setError(null);

        // 1. Fetch latest maturity score (always required)
        const scoreResponse = await authFetch(`/api/journeys/${journeyId}/maturity`);

        if (!scoreResponse.ok) {
          if (scoreResponse.status === 404) {
            setLatestScore(null);
            setLoading(false);
            return;
          }
          throw new Error(`Failed to fetch maturity data: ${scoreResponse.statusText}`);
        }

        const scoreData = await scoreResponse.json();
        setLatestScore(scoreData);

        // 2. Fetch industry comparison (optional - requires organization context)
        if (organizationId) {
          try {
            const orgResponse = await authFetch(`/api/organizations/${organizationId}`);
            if (orgResponse.ok) {
              const orgData = await orgResponse.json();
              const comparisonResponse = await authFetch(
                `/api/journeys/${journeyId}/maturity/comparison?industry=${orgData.industry}&segment=${orgData.segment}`
              );
              if (comparisonResponse.ok) {
                setIndustryComparison(await comparisonResponse.json());
              }
            }
          } catch (err) {
            console.log('[JourneyMaturityPanel] Industry comparison not available:', err);
          }
        }

        // 3. Fetch active target (optional)
        try {
          const targetResponse = await authFetch(`/api/journeys/${journeyId}/maturity/target`);
          if (targetResponse.ok) {
            const targets: TargetApiData[] = await targetResponse.json();
            const active = targets.find(
              (t): t is TargetData => t.status !== 'achieved' && t.status !== 'abandoned'
            );
            setActiveTarget(active ?? null);
          }
        } catch (err) {
          console.log('[JourneyMaturityPanel] Active target not available:', err);
        }

        // 4. Fetch historical data (optional - last 12 months)
        try {
          const historyResponse = await authFetch(`/api/journeys/${journeyId}/maturity/history?limit=12`);
          if (historyResponse.ok) {
            setHistory(await historyResponse.json());
          }
        } catch (err) {
          console.log('[JourneyMaturityPanel] History not available:', err);
        }

      } catch (err: unknown) {
        console.error('[JourneyMaturityPanel] Error:', err);
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      } finally {
        setLoading(false);
      }
    }

    fetchMaturityData();
  }, [journeyId, organizationId, authFetch]);

  if (loading) {
    return (
      <div className="maturity-dashboard-loading">
        <div className="loading-spinner" />
        <div className="loading-text">Loading maturity dashboard...</div>
      </div>
    );
  }

  // Error state - actual API error (not 404)
  if (error) {
    return (
      <div className="maturity-dashboard-error">
        <div className="error-icon">⚠️</div>
        <div className="error-message">Failed to load maturity data</div>
        <div className="error-detail">{error}</div>
      </div>
    );
  }

  // No maturity score yet - show friendly "calculate" prompt
  if (!latestScore) {
    return (
      <div className="maturity-dashboard-empty">
        <div className="empty-icon">📊</div>
        <div className="empty-title">No Maturity Score Yet</div>
        <div className="empty-description">
          Complete at least one station analysis to calculate your journey's maturity score.
          The score combines Data Quality, ML Readiness, Ontology, and FAIR Assessment results.
        </div>
        <div className="empty-hint">
          <strong>Tip:</strong> Start with the Data Quality station for quick results.
        </div>
      </div>
    );
  }

  // Empty state - no maturity score yet
  if (!latestScore.score && !latestScore.overall_score) {
    return (
      <div className="maturity-dashboard-error">
        <div className="error-icon">📊</div>
        <div className="error-message">No Maturity Score Yet</div>
        <div className="error-detail">
          Complete at least one station to see your maturity score.
        </div>
      </div>
    );
  }

  // Extract scores with backward compatibility (camelCase vs snake_case)
  // Convert to numbers safely (API may return strings or numbers)
  const rawScore = latestScore.overall_score ?? latestScore.score ?? 0;
  const score = typeof rawScore === 'string' ? parseFloat(rawScore) : rawScore;
  const rawFairEquivalent = latestScore.fair_equivalent ?? latestScore.fairEquivalent ?? 0;
  const fairEquivalent = typeof rawFairEquivalent === 'string' ? parseFloat(rawFairEquivalent) : rawFairEquivalent;
  const classification = (latestScore.classification ?? 'fair') as MaturityLevel;
  const timestamp = latestScore.created_at ?? latestScore.timestamp ?? new Date().toISOString();
  const stationsCompleted = latestScore.stationsCompleted ?? 0;
  const stationsTotal = latestScore.stationsTotal ?? 7;
  const rawDelta = latestScore.delta;
  const delta = rawDelta !== undefined
    ? (typeof rawDelta === 'string' ? parseFloat(rawDelta) : rawDelta)
    : undefined;

  // Station scores - handle nested structure
  const stationScores = latestScore.stationScores || {};

  // Defensive check - ensure score is valid number
  if (isNaN(score) || isNaN(fairEquivalent)) {
    console.error('[JourneyMaturityPanel] Invalid score data:', { score, fairEquivalent, latestScore });
    return (
      <div className="maturity-dashboard-error">
        <div className="error-icon">⚠️</div>
        <div className="error-message">Invalid Maturity Data</div>
        <div className="error-detail">Score values are not valid numbers</div>
      </div>
    );
  }

  return (
    <div className="maturity-dashboard">
      {/* Hero Section - Always shown */}
      <MaturityScoreHero
        score={score}
        fairEquivalent={fairEquivalent}
        classification={classification}
        delta={delta}
        stationsCompleted={stationsCompleted}
        stationsTotal={stationsTotal}
        timestamp={timestamp}
      />

      {/* Two-column layout for comparison and target */}
      <div className="dashboard-grid-2col">
        {/* Industry Comparison - Optional (requires organization context) */}
        {industryComparison && (
          <IndustryComparisonBar
            yourScore={industryComparison.yourScore}
            industry={industryComparison.industry}
            segment={industryComparison.segment}
            baseline={industryComparison.baseline}
            percentileRank={industryComparison.percentileRank}
            deltaFromBaseline={industryComparison.deltaFromBaseline}
          />
        )}

        {/* Target Progress - Optional (requires active target) */}
        {activeTarget && (
          <TargetProgressCard
            targetScore={activeTarget.targetScore}
            currentScore={activeTarget.currentScore}
            targetDate={activeTarget.targetDate}
            daysRemaining={activeTarget.daysRemaining}
            status={activeTarget.status}
            gap={activeTarget.gap}
            progressPct={activeTarget.progressPct}
            expectedROI={activeTarget.expectedROI}
            rationale={activeTarget.rationale}
            requiredMonthlyRate={activeTarget.requiredMonthlyRate}
          />
        )}
      </div>

      {/* Station Breakdown - Always shown */}
      <StationBreakdownGrid stationScores={stationScores} />

      {/* Historical Trends - Optional (requires history) */}
      {history && history.length > 0 && (
        <HistoricalTrendsChart data={history} />
      )}
    </div>
  );
};
